import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Award, ArrowLeft, Save, Eye, Download, User, Mail,
  BookOpen, Calendar, MapPin, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const GenerateCertificate = () => {
  const [templates, setTemplates] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatedCertId, setGeneratedCertId] = useState(null);
  const [formData, setFormData] = useState({
    participant_name: '',
    participant_email: '',
    course_name: '',
    template_id: '',
    training_date: '',
    duration: '',
    location: '',
    expiry_date: '',
    training_id: ''
  });
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
    fetchTrainings();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/templates?is_active=true`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch templates');

      const data = await response.json();
      setTemplates(data.templates || []);

      // Set default template if available
      const defaultTemplate = data.templates?.find(t => t.is_default);
      if (defaultTemplate && !formData.template_id) {
        setFormData(prev => ({ ...prev, template_id: defaultTemplate.id }));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const fetchTrainings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/training-calendar?is_active=true&limit=100`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch trainings');

      const data = await response.json();
      setTrainings(data.trainings || []);
    } catch (error) {
      console.error('Error fetching trainings:', error);
    }
  };

  const handleTrainingSelect = (trainingId) => {
    const training = trainings.find(t => t.id === parseInt(trainingId));
    if (training) {
      setFormData(prev => ({
        ...prev,
        training_id: trainingId,
        course_name: training.title || '',
        training_date: training.date ? training.date.split('T')[0] : '',
        duration: training.duration || '',
        location: training.location || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.template_id) {
      toast.error('Please select a template');
      return;
    }

    try {
      setGenerating(true);
      const formDataToSend = new FormData();

      formDataToSend.append('participant_name', formData.participant_name);
      formDataToSend.append('participant_email', formData.participant_email);
      formDataToSend.append('course_name', formData.course_name);
      formDataToSend.append('template_id', formData.template_id);

      if (formData.training_date) formDataToSend.append('training_date', formData.training_date);
      if (formData.duration) formDataToSend.append('duration', formData.duration);
      if (formData.location) formDataToSend.append('location', formData.location);
      if (formData.expiry_date) formDataToSend.append('expiry_date', formData.expiry_date);
      if (formData.training_id) formDataToSend.append('training_id', formData.training_id);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/generate`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataToSend
        }
      );

      if (!response.ok) throw new Error('Failed to generate certificate');

      const data = await response.json();
      setGeneratedCertId(data.certificate.id);

      toast.success(`Certificate generated! ID: ${data.certificate_id}`);

      // Ask if user wants to download
      if (window.confirm('Certificate generated successfully! Do you want to download it now?')) {
        handleDownload(data.certificate.id, data.certificate_id);
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error('Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (certId, certificateId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/download/${certId}`
      );

      if (!response.ok) throw new Error('Failed to download certificate');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificate_${certificateId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Certificate downloaded successfully');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    }
  };

  const handlePreview = async () => {
    if (!generatedCertId) {
      toast.error('Please generate a certificate first');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/preview/${generatedCertId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to preview certificate');

      const html = await response.text();
      const newWindow = window.open('', '_blank');
      newWindow.document.write(html);
      newWindow.document.close();
    } catch (error) {
      console.error('Error previewing certificate:', error);
      toast.error('Failed to preview certificate');
    }
  };

  const resetForm = () => {
    setFormData({
      participant_name: '',
      participant_email: '',
      course_name: '',
      template_id: templates.find(t => t.is_default)?.id || '',
      training_date: '',
      duration: '',
      location: '',
      expiry_date: '',
      training_id: ''
    });
    setGeneratedCertId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/admin/certificates')}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Generate Certificate</h1>
            <p className="text-gray-600 mt-1">Create a certificate for a participant</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Participant Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Participant Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.participant_name}
                    onChange={(e) => setFormData({ ...formData, participant_name: e.target.value })}
                    placeholder="Enter participant's full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.participant_email}
                    onChange={(e) => setFormData({ ...formData, participant_email: e.target.value })}
                    placeholder="participant@example.com"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Training Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Training Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select from Existing Training (Optional)
                  </label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.training_id}
                    onChange={(e) => handleTrainingSelect(e.target.value)}
                  >
                    <option value="">-- Or enter manually below --</option>
                    {trainings.map((training) => (
                      <option key={training.id} value={training.id}>
                        {training.title} - {new Date(training.date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.course_name}
                    onChange={(e) => setFormData({ ...formData, course_name: e.target.value })}
                    placeholder="e.g., ISO 9001:2015 Lead Auditor Training"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="h-4 w-4 inline mr-1" />
                      Training Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.training_date}
                      onChange={(e) => setFormData({ ...formData, training_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Duration
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      placeholder="e.g., 5 Days, 40 Hours"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="h-4 w-4 inline mr-1" />
                    Location
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Bangalore, India"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Template Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Certificate Template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Template *
                  </label>
                  <select
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.template_id}
                    onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                  >
                    <option value="">Select a template...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.template_name}
                        {template.is_default ? ' (Default)' : ''}
                      </option>
                    ))}
                  </select>
                  {templates.length === 0 && (
                    <p className="text-sm text-amber-600 mt-2">
                      No templates available. Please create a template first.
                    </p>
                  )}
                </div>

                {formData.template_id && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-900">
                      {templates.find(t => t.id === parseInt(formData.template_id))?.template_type}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  type="submit"
                  disabled={generating || !formData.template_id}
                  className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
                >
                  {generating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Award className="h-4 w-4" />
                      Generate Certificate
                    </>
                  )}
                </Button>

                {generatedCertId && (
                  <>
                    <Button
                      type="button"
                      onClick={handlePreview}
                      variant="outline"
                      className="w-full gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      Preview Certificate
                    </Button>

                    <Button
                      type="button"
                      onClick={() => {
                        const cert = { id: generatedCertId };
                        handleDownload(cert.id, formData.participant_name.replace(/\s+/g, '_'));
                      }}
                      variant="outline"
                      className="w-full gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </Button>
                  </>
                )}

                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="w-full"
                >
                  Reset Form
                </Button>
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h4 className="font-medium text-blue-900 mb-2">💡 Quick Tips</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Fill all required fields (*)</li>
                  <li>• Select an existing training to auto-fill details</li>
                  <li>• Certificate ID is auto-generated</li>
                  <li>• Download PDF after generation</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};
